/**
 * Agreements — every Vehicle Schedule this partner is a party to, at `/agreements`.
 *
 * ── ONE CARD, ONE ACTION ──────────────────────────────────────────────────────
 * A card names the vehicle the Schedule is about and carries exactly one thing to do, chosen
 * by the state the partner is actually in:
 *
 *   waiting for me      Sign now        the ceremony, at `/sign/:id`
 *   executed            View            the at-rest reader, at `/agreement/:id`
 *   signed, not sealed  View document   back into the ceremony, where the frozen text is
 *                                       still readable before the executed PDF exists
 *   withdrawn / other   nothing, and a sentence saying so (`inactionReason`, in `esign.ts`
 *                       with the predicates it is the other half of)
 *
 * ── TWO HEADINGS, BECAUSE ONE OF THEM IS A REQUEST ───────────────────────────
 * `groupAgreementDocuments` splits the roster on an allowlist of the two statuses in which a
 * signature can still be taken. Everything else moves below, where a partner who has already
 * signed can still find what they signed — a finished Schedule under a heading that asks for
 * a signature is an ask nobody can satisfy.
 *
 * ── THE LIST IS THE SESSION'S ─────────────────────────────────────────────────
 * `get_my_partner_agreements()` takes no arguments: it answers for `my_partner_id()` and for
 * nobody else, keyed on the STAMPED referent partner — so a vehicle that has changed hands
 * leaves the executed Schedule with the partner who executed it, and this page needs no
 * partner id to be right about whose documents these are.
 *
 * ── NO FIGURES ON THE CARD ────────────────────────────────────────────────────
 * The roster carries the Cash price (its `all_in_cost`: copied from the contract at generation
 * for a schedule generated from [D-B59] on, the figure the partner stated under [D-B48] on an
 * earlier one) and the management rate, and they are deliberately not printed here.
 * The Schedule states them in the server's own formatting, and a card that spelled the same
 * amount a second way would be a second spelling of the money for a partner to reconcile. The
 * figures live on the ceremony's intro step and inside the instrument.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, FileSignature, PenLine, RotateCw } from 'lucide-react';

import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { formatBusinessDate } from '@/lib/format';
import { termsCardCaption } from '@/lib/agreement-terms-authority';
import {
    agreementViewable,
    awaitingMySignatureOnRow,
    documentLabel,
    groupAgreementDocuments,
    inactionReason,
} from '@/lib/esign';
import {
    usePartnerAgreements,
    type PartnerAgreementSummary,
} from '@/hooks/useAgreements';
import SectionDrawer from '@/components/navigation/SectionDrawer';

function statusBadge(row: PartnerAgreementSummary) {
    switch (row.status) {
        case 'COMPLETED':
            return { label: row.finalized_at ? 'Complete' : 'Signed', variant: 'default' as const };
        case 'IN_SIGNING':
            return { label: 'In signing', variant: 'secondary' as const };
        case 'PENDING_REVIEW':
            return { label: 'Being prepared', variant: 'outline' as const };
        case 'VOIDED':
            return { label: 'Withdrawn', variant: 'destructive' as const };
        case 'SUPERSEDED':
            return { label: 'Replaced', variant: 'outline' as const };
        default:
            return { label: row.status, variant: 'outline' as const };
    }
}

export default function Agreements() {
    const navigate = useNavigate();
    const { signOut } = useAuth();
    const { data, isLoading, error, refetch } = usePartnerAgreements();

    const handleSignOut = async () => {
        await signOut();
        navigate('/auth');
    };

    const handleNavChange = (section: string) => {
        navigate(`/${section}`);
    };

    const { live, past } = useMemo(() => groupAgreementDocuments(data), [data]);
    const total = live.length + past.length;

    const renderCard = (row: PartnerAgreementSummary) => {
        const badge = statusBadge(row);
        const awaitingMe = awaitingMySignatureOnRow(row);
        const viewable = agreementViewable(row);
        const iSigned = row.signature_status === 'SIGNED';
        // Present only once the cited booklet resolves on the row; absent until then, and
        // never composed from a guess.
        const caption = termsCardCaption(row.terms_form_code, row.terms_version);
        // THE ACTION HAS TO CARRY THE VEHICLE. Every card's button says the same three words,
        // and the vehicle is in a separate <p> with no programmatic relationship to it — so a
        // partner with four vehicles navigating by button list is offered four identical,
        // indistinguishable choices. The visible words OPEN each accessible name, so voice
        // control still matches on what is printed on the button [WCAG 2.5.3].
        const vehicle = row.vehicle_label ?? 'vehicle not recorded';

        return (
            <Card key={row.document_id} className="overflow-hidden">
                <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <div className="rounded-lg bg-muted p-2 text-foreground">
                                <FileSignature className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold">
                                    {row.vehicle_label ?? 'Vehicle not recorded'}
                                </p>
                                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                                    {documentLabel(row.title, 'PARTNER_MANAGEMENT')}
                                </p>
                                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                                    {row.reference_no}
                                    {row.finalized_at
                                        ? ` · completed ${formatBusinessDate(row.finalized_at)}`
                                        : ` · raised ${formatBusinessDate(row.created_at)}`}
                                </p>
                            </div>
                        </div>
                        <Badge variant={badge.variant} className="shrink-0">
                            {badge.label}
                        </Badge>
                    </div>
                </CardHeader>

                <CardContent className="space-y-3 pt-0">
                    {awaitingMe && (
                        <Button
                            className="w-full"
                            aria-label={`Sign now — ${vehicle}`}
                            onClick={() => navigate(`/sign/${row.document_id}`)}
                        >
                            <PenLine className="h-4 w-4" />
                            Sign now
                        </Button>
                    )}

                    {!awaitingMe && viewable && (
                        <div className="space-y-1.5">
                            <Button
                                variant="outline"
                                className="w-full"
                                aria-label={`View — ${vehicle}`}
                                onClick={() => navigate(`/agreement/${row.document_id}`)}
                            >
                                <Eye className="h-4 w-4" />
                                View
                            </Button>
                            {caption && (
                                <p className="text-center text-[11px] text-muted-foreground">
                                    {caption}
                                </p>
                            )}
                        </div>
                    )}

                    {/* SIGNED BUT NOT SEALED. There is no executed PDF yet, so the only copy
                        of what was signed is the frozen text the ceremony still shows. */}
                    {!awaitingMe && !viewable && iSigned && (
                        <div className="space-y-1.5">
                            <Button
                                variant="outline"
                                className="w-full"
                                aria-label={`View document — ${vehicle}`}
                                onClick={() => navigate(`/sign/${row.document_id}`)}
                            >
                                <Eye className="h-4 w-4" />
                                View document
                            </Button>
                            <p className="text-center text-[11px] text-muted-foreground">
                                You have signed. Primelane countersigns next.
                            </p>
                        </div>
                    )}

                    {!awaitingMe && !viewable && !iSigned && (
                        <p className="text-[11.5px] text-muted-foreground">
                            {inactionReason(row)}
                        </p>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Mobile nav */}
            <div className="lg:hidden">
                <SectionDrawer
                    activeSection="agreements"
                    onSectionChange={handleNavChange}
                    onSignOut={handleSignOut}
                />
            </div>

            <div>
                <h1 className="text-3xl font-semibold tracking-tight mb-2">Documents</h1>
                <p className="text-muted-foreground">
                    The Vehicle Schedule for each vehicle you have placed with Primelane.
                </p>
            </div>

            {isLoading && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Skeleton className="h-44 w-full rounded-xl" />
                    <Skeleton className="h-44 w-full rounded-xl" />
                </div>
            )}

            {/* THE READ FAILING IS SAID OUT LOUD. A documents page that silently showed
                nothing would tell a partner their schedules do not exist. */}
            {!isLoading && error && (
                /* "Try again in a moment" with nothing to press left the only recovery as a
                   full page reload. The query already exposes `refetch`, and `EmptyState`
                   already renders an action. */
                <EmptyState
                    icon={<FileSignature />}
                    title="We could not load your documents"
                    description="Something went wrong reading your Vehicle Schedules. Try again, or contact Primelane if it keeps happening."
                    action={
                        <Button variant="outline" onClick={() => void refetch()}>
                            <RotateCw className="h-4 w-4" />
                            Try again
                        </Button>
                    }
                />
            )}

            {!isLoading && !error && total === 0 && (
                <EmptyState
                    icon={<FileSignature />}
                    title="No Vehicle Schedules yet"
                    description="A Vehicle Schedule appears here for each vehicle you place with Primelane, ready for you to read and sign."
                />
            )}

            {!isLoading && !error && live.length > 0 && (
                <div>
                    <h2 className="mb-4 text-lg font-semibold">Agreements to sign</h2>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {live.map(renderCard)}
                    </div>
                </div>
            )}

            {!isLoading && !error && past.length > 0 && (
                <div>
                    <h2 className="mb-4 text-lg font-semibold">Signed &amp; past agreements</h2>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {past.map(renderCard)}
                    </div>
                </div>
            )}
        </div>
    );
}
